import type {
  KycCompanyType,
  KycCompliance,
  KycUserType,
} from '@/types/KycCompliance';

export function isComplianceNormal(company: KycCompanyType) {
  return (
    company?.complianceStatus === 'NORMAL' ||
    company?.complianceStatus === 'UNDER_COMPLIANCE_REVIEW' ||
    company?.complianceStatus === 'UNDER_COMPLIANCE_REVIEW_2' ||
    company?.complianceStatus === 'REVISION'
  );
}

export function isCompanyKAM(kyc: KycCompliance) {
  return (
    kyc?.company?.kycStage1 === 'REJECTED_MANUAL' &&
    (kyc?.company?.complianceStatus === 'FULLY_BLOCKED' ||
      kyc?.company?.complianceStatus === 'BLOCKED')
  );
}

export function isCompanyRejected(kyc: KycCompliance) {
  return (
    kyc?.company?.kycStage1 === 'REJECTED_MANUAL' &&
    (isComplianceNormal(kyc?.company) ||
      kyc?.company?.complianceStatus === 'WARNING')
  );
}

export function isUserIncomplete(kyc: KycUserType) {
  return kyc?.kycStage1 === 'REQUESTED_MANUAL';
}

export function isUserProcess(kyc: KycUserType) {
  return kyc?.kycStage1 === 'UPLOADED_MANUAL';
}

export function isUserComplete(kyc: KycUserType) {
  return kyc?.kycStage1 === 'APPROVED';
}

export function isUserRejected(kyc: KycUserType) {
  return kyc?.kycStage1 === 'REJECTED_MANUAL';
}

export function isCompanyIncomplete(kyc: KycCompliance) {
  return kyc?.company?.kycStage1 === 'REQUESTED_MANUAL';
}

export function isCompanyPending(kyc: KycCompliance) {
  return kyc?.company?.kycStage1 === 'UPLOADED_MANUAL';
}

export function isCompanyComplete(company: KycCompanyType) {
  return company?.kycStage1 === 'APPROVED' && isComplianceNormal(company);
}

export function isCompanyCompleteWarning(company: KycCompanyType) {
  return (
    company?.kycStage1 === 'APPROVED' && company?.complianceStatus === 'WARNING'
  );
}

export function isOnboardingCompleted(kyc: KycCompliance) {
  return (
    kyc?.b2bUser?.kycStage1 === 'APPROVED' &&
    kyc?.company?.kycStage1 === 'APPROVED' &&
    (isComplianceNormal(kyc?.company) ||
      kyc?.company?.complianceStatus === 'WARNING')
  );
}

export function isCompanyFullyBlocked(kyc: KycCompliance) {
  return kyc?.company?.complianceStatus === 'FULLY_BLOCKED';
}

export function isCompanyBlocked(kyc: KycCompliance) {
  return kyc?.company?.complianceStatus === 'BLOCKED';
}

export function isKycWarningCompany(kyc: KycCompliance) {
  const { company } = kyc;

  return (
    company?.kycStage1 === 'APPROVED' && company?.complianceStatus === 'WARNING'
  );
}

export function isUnlockedRouteKyc(name = '') {
  const listWhiteRoute = [
    'log-in',
    'onboarding',
    'onboarding-index',
    'profile',
    'profile-index',
    'profile-success',
    'verify-phone-otp',
    'onboarding-invitation',
    'shareholders-declaration',
    'shareholders-declaration-colombia',
  ];

  return listWhiteRoute.includes(name);
}

export function isUserVerifiedPhone(user: KycUserType) {
  return user?.verifiedPhone;
}

export function isCustomerReview(kyc: KycCompliance): boolean {
  return kyc?.company?.complianceStatus === 'CUSTOMER_REVIEW';
}

export function isUserLegalRepresentative(kyc: KycCompliance) {
  return kyc?.b2bUser?.isLegalRepresentative;
}

export function isUserRequestedPartner(kyc: KycCompliance) {
  return kyc?.b2bUser?.kycStage1 === 'REQUESTED_PARTNER';
}

interface CanCreateStablecoinWalletParams {
  isInstitutional: boolean;
  companyId: string;
  generalEnabled: boolean;
  institutionalEnabled: boolean;
  institutionalWhitelist: string[];
}

/**
 * Control de UI para mostrar la opción de crear wallet stablecoin.
 * Los no institucionales se rigen por el flag general (comportamiento actual);
 * los institucionales solo por la whitelist institucional (el flag general no aplica).
 */
export function canCreateStablecoinWallet(
  params: CanCreateStablecoinWalletParams
): boolean {
  const {
    isInstitutional,
    companyId,
    generalEnabled,
    institutionalEnabled,
    institutionalWhitelist,
  } = params;

  if (!isInstitutional) return generalEnabled;
  if (institutionalEnabled) return true;
  return Boolean(companyId) && (institutionalWhitelist ?? []).includes(companyId);
}

/**
 * Determina si se debe mostrar el flujo de onboarding autogestionado.
 * @param kyc - Datos de compliance del KYC
 * @returns true si se debe mostrar el onboarding autogestionado, false en caso contrario
 */
export function shouldShowSelfManagedOnboarding(
  kyc: KycCompliance
): boolean {
  const company = kyc?.company;
  if (!company) return false;

  const isValidKycStage =
    company.kycStage1 === 'UPLOADED_MANUAL' ||
    company.kycStage1 === 'REJECTED_MANUAL';

  const allowedStatuses = [
    'NORMAL',
    'CUSTOMER_REVIEW',
    'PENDING_COMPLIANCE',
  ];

  return (
    isValidKycStage &&
    allowedStatuses.includes(company.complianceStatus)
  );
}
